import { createSlice } from "@reduxjs/toolkit"
import { pick, uniqBy, union } from "lodash-es"
import { capitalize } from "utils/stringHelpers"
import {
  PUBLIC_STATUS_BOUNCED,
  PUBLIC_STATUS_DELIVERED,
  PUBLIC_STATUS_PENDING,
  PUBLIC_STATUS_SUBMITTED,
} from "utils/surveyHelpers"
import { sortByDateTime, sortByFormName, sortByText } from "shared/Table/TableDataElement/tableSortingHelper"

export const STATUS_FILTER_OPTIONS = [
  PUBLIC_STATUS_PENDING,
  PUBLIC_STATUS_DELIVERED,
  PUBLIC_STATUS_BOUNCED,
  PUBLIC_STATUS_SUBMITTED,
].map((value) => ({ label: capitalize(value), value }))

const recipientName = ({ contact }) => `${contact.lastName ?? ""}, ${contact.firstName ?? ""}`

const sortByRecipientName = (items, { isAsc }) => items.sort((sub1, sub2) => (
  isAsc
    ? recipientName(sub1).localeCompare(recipientName(sub2))
    : recipientName(sub2).localeCompare(recipientName(sub1))
))

const sortByContactEmail = (items, { isAsc }) => items.sort((sub1, sub2) => {
  const val1 = sub1.contact.email ?? ""
  const val2 = sub2.contact.email ?? ""
  return isAsc ? val1.localeCompare(val2) : val2.localeCompare(val1)
})

export const FORM_COLUMN = { label: "Form", value: "form" }
export const BATCH_DATE_COLUMN = { label: "Batch Date", value: "batchDate" }
export const BATCH_NAME_COLUMN = { label: "Batch Name", value: "batchName" }
export const RECIPIENT_NAME_COLUMN = { label: "Recipient Name", value: "recipientName" }
export const EMAIL_COLUMN = { label: "Email", value: "email" }
export const DELIVERY_STATUS_COLUMN = { label: "Delivery Status", value: "publicStatus" }

export const COLUMNS = [
  FORM_COLUMN,
  BATCH_DATE_COLUMN,
  BATCH_NAME_COLUMN,
  RECIPIENT_NAME_COLUMN,
  EMAIL_COLUMN,
  DELIVERY_STATUS_COLUMN,
]

// Keyed by column.value. Kept out of the column objects themselves so Redux
// state (which stores the current column) never holds a function reference.
const SORTING_METHODS = {
  [FORM_COLUMN.value]: sortByFormName,
  [BATCH_DATE_COLUMN.value]: sortByDateTime,
  [BATCH_NAME_COLUMN.value]: sortByText,
  [RECIPIENT_NAME_COLUMN.value]: sortByRecipientName,
  [EMAIL_COLUMN.value]: sortByContactEmail,
  [DELIVERY_STATUS_COLUMN.value]: sortByText,
}

const INITIAL_STATE = {
  surveyRecipients: [],
  formIdsToHide: [],
  statusesToHide: [],
  currentSort: {
    column: BATCH_DATE_COLUMN,
    isAsc: false,
  },
}

export const surveyRecipientTableSlice = createSlice({
  name: "surveyRecipientTable",
  initialState: INITIAL_STATE,
  reducers: {
    hideAllForms: (state) => {
      const formIds = uniqBy(state.surveyRecipients.map(({ form }) => form), "id")
        .map((form) => form.id)

      state.formIdsToHide = union(state.formIdsToHide, formIds)
    },
    hideAllStatuses: (state) => {
      state.statusesToHide = STATUS_FILTER_OPTIONS.map(({ value }) => value)
    },
    hideForm: (state, action) => {
      state.formIdsToHide.push(action.payload)
    },
    hideStatus: (state, action) => {
      state.statusesToHide.push(action.payload)
    },
    setFilterSelections: (state, action) => {
      const {
        currentSort = INITIAL_STATE.currentSort,
        formIdsToHide = [],
        statusesToHide = [],
      } = action.payload ?? {}

      state.currentSort = currentSort
      state.formIdsToHide = formIdsToHide
      state.statusesToHide = statusesToHide
    },
    setCurrentSort: (state, action) => {
      const selectedColumn = action.payload

      if (state.currentSort.column.value === selectedColumn.value) {
        state.currentSort.isAsc = !state.currentSort.isAsc
      } else {
        state.currentSort = {
          column: selectedColumn,
          isAsc: true,
        }
      }
    },
    setSurveyRecipients: (state, action) => {
      state.surveyRecipients = action.payload
    },
    showAllForms: (state) => {
      state.formIdsToHide = []
    },
    showAllStatuses: (state) => {
      state.statusesToHide = []
    },
    showForm: (state, action) => {
      state.formIdsToHide = state.formIdsToHide.filter((id) => id !== action.payload)
    },
    showStatus: (state, action) => {
      state.statusesToHide = state.statusesToHide.filter((status) => status !== action.payload)
    },
  },
})

export const {
  hideAllForms,
  hideAllStatuses,
  hideForm,
  hideStatus,
  setCurrentSort,
  setFilterSelections,
  setSurveyRecipients,
  showAllForms,
  showAllStatuses,
  showForm,
  showStatus,
} = surveyRecipientTableSlice.actions

export const getCurrentSort = (state) => state.surveyRecipientTable.currentSort
export const getFormIdsToHide = (state) => state.surveyRecipientTable.formIdsToHide
export const getStatusesToHide = (state) => state.surveyRecipientTable.statusesToHide

export const getForms = ({ surveyRecipientTable }) => uniqBy(
  surveyRecipientTable.surveyRecipients.map(({ form }) => form),
  "id",
)

export const getFormCounts = ({ surveyRecipientTable }) => surveyRecipientTable.surveyRecipients
  .reduce((counts, { form }) => ({ ...counts, [form.id]: (counts[form.id] || 0) + 1 }), {})

export const getStatusCounts = ({ surveyRecipientTable }) => surveyRecipientTable.surveyRecipients
  .reduce((counts, { publicStatus }) => (
    { ...counts, [publicStatus]: (counts[publicStatus] || 0) + 1 }
  ), {})

export const getSurveyRecipients = ({ surveyRecipientTable }) => {
  const {
    currentSort, formIdsToHide, statusesToHide, surveyRecipients,
  } = surveyRecipientTable

  const filteredRecipients = surveyRecipients
    .filter(({ form }) => !formIdsToHide.includes(form.id))
    .filter(({ publicStatus }) => !statusesToHide.includes(publicStatus))

  const sortingMethod = SORTING_METHODS[currentSort.column.value]

  return sortingMethod(filteredRecipients, currentSort)
}

export const settingsToPersist = (state) => (
  pick(state.surveyRecipientTable, ["currentSort", "formIdsToHide", "statusesToHide"])
)

export default surveyRecipientTableSlice.reducer
